import { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { api } from "../lib/api";
import { clearSession, getSession } from "../lib/auth";
import { useMe } from "../lib/me";
import { useTheme } from "../lib/theme";
import { Icon } from "./Icon";
import { StaleSyncBanner } from "./StaleSyncBanner";

function isEnrollmentMode(): boolean {
  return !!getSession()?.must_enroll;
}

type NavItem = {
  to: string;
  label: string;
  icon: string;
  badge?: number;
  badgeKind?: "err" | "warn";
  masterOnly?: boolean;
};

const NAV: NavItem[] = [
  { to: "/overview", label: "Dashboard", icon: "dashboard" },
  { to: "/health", label: "Saúde", icon: "activity" },
  { to: "/sites", label: "Sites", icon: "sites" },
  { to: "/domains", label: "Domínios", icon: "domains" },
  { to: "/hosting-accounts", label: "Hosting", icon: "hosting" },
  { to: "/expirations", label: "Expirações", icon: "expiry" },
  { to: "/import", label: "Importar", icon: "import" },
  { to: "/audit", label: "Auditoria", icon: "audit" },
];

const NAV_ACCOUNT: NavItem[] = [
  { to: "/settings/smtp", label: "SMTP", icon: "mail" },
  { to: "/settings/whatsapp", label: "WhatsApp", icon: "wa" },
  { to: "/security", label: "Segurança (2FA)", icon: "lock" },
  { to: "/security-audit", label: "Audit Segurança", icon: "shield", masterOnly: true },
  { to: "/users", label: "Usuários", icon: "users", masterOnly: true },
];

export function Layout({ children }: { children: ReactNode }) {
  const { theme, toggle } = useTheme();
  const me = useMe();
  const isMaster = me.data?.role === "master";
  const enrollmentMode = isEnrollmentMode();

  const initial = me.data?.email
    ? me.data.email[0].toUpperCase()
    : me.data?.is_env_token
    ? "A"
    : "?";

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      {/* Sidebar */}
      <aside
        className="flex flex-col flex-shrink-0"
        style={{
          width: 240,
          height: "100vh",
          position: "sticky",
          top: 0,
          overflowY: "auto",
          background: "var(--surface)",
          borderRight: "1px solid var(--border)",
          padding: "20px 12px",
          gap: 2,
        }}
      >
        {/* Logo */}
        <div style={{ padding: "4px 8px 22px" }}>
          <MELogo />
        </div>

        {enrollmentMode ? (
          // Fase C — sidebar bloqueada. Mostra só um aviso pra o user
          // não tentar navegar pra outra rota; o EnrollmentGuard em
          // App.tsx redireciona qualquer URL pra /security mesmo, mas
          // explicar o motivo evita confusão.
          <div
            style={{
              padding: 14,
              borderRadius: 10,
              background: "color-mix(in srgb, var(--brand-500) 12%, transparent)",
              border: "1px solid color-mix(in srgb, var(--brand-500) 30%, transparent)",
              fontSize: 12,
              color: "var(--text)",
              lineHeight: 1.5,
            }}
          >
            <strong style={{ display: "block", marginBottom: 6, color: "var(--brand-500)" }}>
              Cadastre o 2FA
            </strong>
            <p style={{ margin: 0, color: "var(--muted)" }}>
              Master é obrigado a usar segundo fator. Conclua o cadastro
              ao lado pra liberar o painel.
            </p>
          </div>
        ) : (
          <>
            {/* Monitoramento nav */}
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--muted-2)",
                padding: "6px 10px 4px",
              }}
            >
              Monitoramento
            </div>
            {NAV.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}

            <div style={{ flex: 1 }} />

            {/* Conta nav */}
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--muted-2)",
                padding: "12px 10px 4px",
              }}
            >
              Conta
            </div>
            {NAV_ACCOUNT.filter((item) => !item.masterOnly || isMaster).map(
              (item) => (
                <SidebarLink key={item.to} item={item} />
              )
            )}
          </>
        )}
        {enrollmentMode && <div style={{ flex: 1 }} />}

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggle}
          title={theme === "dark" ? "Modo claro" : "Modo escuro"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid transparent",
            background: "transparent",
            color: "var(--muted)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            marginTop: 4,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = "var(--elevated)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = "transparent";
          }}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
          <span>{theme === "dark" ? "Modo claro" : "Modo escuro"}</span>
        </button>

        {/* User profile */}
        {me.data ? (
          <div
            style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 10,
              background: "var(--elevated)",
              border: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "linear-gradient(135deg, var(--brand-500), var(--accent-500))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {initial}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {me.data.is_env_token ? "ADMIN_TOKEN" : me.data.email}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "capitalize",
                }}
              >
                Engrene · {me.data.role}
              </div>
            </div>
            <button
              type="button"
              title="Sair"
              onClick={async () => {
                if (getSession()) {
                  try {
                    await api("/auth/logout", { method: "POST" });
                  } catch {
                    // logout local segue mesmo se o backend falhar
                  }
                }
                clearSession();
                window.dispatchEvent(new Event("admin-token-cleared"));
              }}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--muted-2)",
                padding: 4,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--err-500)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--muted-2)";
              }}
            >
              <Icon name="logout" size={14} />
            </button>
          </div>
        ) : null}
      </aside>

      {/* Main */}
      <main
        className="flex-1 overflow-auto"
        style={{ background: "var(--bg)" }}
      >
        <StaleSyncBanner />
        {children}
      </main>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const badgeColor =
    item.badgeKind === "err"
      ? "var(--err-500)"
      : item.badgeKind === "warn"
      ? "var(--warn-500)"
      : "var(--brand-500)";

  return (
    <NavLink
      to={item.to}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 10,
        border: isActive ? "1px solid var(--border)" : "1px solid transparent",
        background: isActive ? "var(--elevated)" : "transparent",
        color: isActive ? "var(--text)" : "var(--muted)",
        fontSize: 13,
        fontWeight: isActive ? 600 : 500,
        textDecoration: "none",
        position: "relative",
        cursor: "pointer",
        transition: "background 0.12s, color 0.12s",
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 8,
                bottom: 8,
                width: 3,
                borderRadius: 2,
                background: "var(--brand-500)",
              }}
            />
          )}
          <Icon name={item.icon} size={17} />
          <span style={{ flex: 1 }}>{item.label}</span>
          {item.badge != null && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: 999,
                background: item.badgeKind
                  ? `color-mix(in srgb, ${badgeColor} 18%, transparent)`
                  : "var(--elevated)",
                color: item.badgeKind ? badgeColor : "var(--muted)",
                minWidth: 18,
                textAlign: "center",
              }}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function MELogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <svg width={26} height={26} viewBox="0 0 32 32" fill="none" style={{ flexShrink: 0 }}>
        <g fill="var(--brand-500)">
          {([0, 45, 90, 135, 180, 225, 270, 315] as number[]).map((a, i) => (
            <rect
              key={i}
              x="14.5"
              y="0.5"
              width="3"
              height="5"
              rx="0.6"
              transform={`rotate(${a} 16 16)`}
            />
          ))}
        </g>
        <circle cx="16" cy="16" r="10.5" fill="var(--brand-500)" />
        <circle cx="16" cy="16" r="4" fill="var(--bg, #0a0a0a)" />
        <circle cx="24" cy="9" r="2.4" fill="var(--accent-500)" />
        <circle cx="24" cy="9" r="1" fill="var(--cyan-engrene)" />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span
          style={{
            fontFamily: "Montserrat, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            letterSpacing: "-0.02em",
            color: "var(--text)",
          }}
        >
          monitora
        </span>
        <span
          style={{
            fontFamily: "Montserrat, system-ui, sans-serif",
            fontWeight: 500,
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--muted)",
          }}
        >
          engrene
        </span>
      </div>
    </div>
  );
}
